import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Label, Message, Modal, Progress, Table } from 'semantic-ui-react';
import { stockyApi } from '../misc/StockyApi';

const MAX_BYTES = 5 * 1024 * 1024;

const readBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('No se pudo leer la planilla.'));
  reader.onload = () => resolve(String(reader.result || '').split(',').pop() || '');
  reader.readAsDataURL(file);
});

export default function SkuMigrationImportModal({ open, onClose, authUser, onCompleted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setFile(null); setPreview(null); setProgress(null); setLoading(false); setError('');
    }
  }, [open]);

  useEffect(() => {
    if (!progress?.importId || progress.status !== 'RUNNING' || !authUser) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const response = await stockyApi.getSkuMigrationImportStatus(progress.importId, authUser);
        const next = response?.data;
        setProgress(next);
        if (next?.status === 'COMPLETED') onCompleted?.();
      } catch (requestError) {
        setError(requestError?.response?.data?.message || 'No se pudo consultar la migracion.');
      }
    }, 1200);
    return () => window.clearInterval(timer);
  }, [authUser, onCompleted, progress?.importId, progress?.status]);

  const validRows = Number(preview?.summary?.validRows || 0);
  const ignoredRows = Number(preview?.summary?.ignoredRows || 0);
  const invalidRows = Number(preview?.summary?.invalidRows || 0);
  const warningRows = Number(preview?.summary?.warningRows || 0);
  const previewRows = useMemo(() => [...(preview?.rows || [])].sort((a, b) => Number(a.rowNumber) - Number(b.rowNumber)), [preview]);

  const handleFile = (event) => {
    const next = event.target.files?.[0] || null;
    setPreview(null); setProgress(null); setError('');
    if (!next) { setFile(null); return; }
    if (!next.name.toLowerCase().endsWith('.xlsx')) { setFile(null); setError('Cargá la planilla XLSX corregida.'); return; }
    if (next.size > MAX_BYTES) { setFile(null); setError('La planilla no puede superar 5 MB.'); return; }
    setFile(next);
  };

  const handlePreview = async () => {
    if (!file || !authUser) return;
    setLoading(true); setError('');
    try {
      const fileContentBase64 = await readBase64(file);
      const response = await stockyApi.previewSkuMigrationImport({ fileContentBase64 }, authUser);
      setPreview(response?.data || null);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'No se pudo validar la planilla.');
    } finally { setLoading(false); }
  };

  const handleExecute = async () => {
    if (!preview?.draftId || !authUser) return;
    setLoading(true); setError('');
    try {
      const response = await stockyApi.executeSkuMigrationImport({ draftId: preview.draftId }, authUser);
      setProgress({
        importId: response?.data?.importId,
        status: 'RUNNING',
        totalGroups: preview.summary?.skuGroups,
        processedGroups: 0,
        omittedRows: preview.rows?.filter((row) => row.outcome !== 'APPLY') || [],
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo iniciar la migracion.');
    } finally { setLoading(false); }
  };

  const completion = progress?.status === 'COMPLETED';
  const pct = progress?.totalGroups ? Math.round((Number(progress.processedGroups || 0) / Number(progress.totalGroups)) * 100) : 0;

  return <Modal open={open} onClose={loading || progress?.status === 'RUNNING' ? undefined : onClose} size="large">
    <Modal.Header>Migrar SKU de Mercado Libre</Modal.Header>
    <Modal.Content scrolling>
      <Message warning>
        Esta herramienta actualiza el SKU en MercuryCom y en Mercado Libre. Solo acepta una cuenta de Mercado Libre por planilla y no cambia el stock.
      </Message>
      {!progress ? <>
        <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFile} />
        {file ? <Message info content={`Archivo listo: ${file.name}`} /> : null}
        <Button primary onClick={handlePreview} disabled={!file || loading} loading={loading} style={{ marginTop: 12 }}><Icon name="search" />Validar planilla</Button>
      </> : <>
        <Progress percent={pct} progress indicating={progress.status === 'RUNNING'} />
        <p>{progress.status === 'RUNNING' ? `Procesando ${progress.processedGroups || 0} de ${progress.totalGroups || 0} grupos.` : `Finalizado: ${progress.updatedGroups || 0} grupos actualizados, ${progress.failedGroups || 0} con error y ${(progress.omittedRows || []).length} filas omitidas.`}</p>
        {(progress.results || []).filter((result) => result.status === 'FAILED').map((result) => <Message key={result.nextSku} negative content={`${result.nextSku}: ${result.error || 'No se pudo completar.'}`} />)}
        {progress.status === 'COMPLETED' && (progress.omittedRows || []).map((row) => <Message key={`${row.rowNumber}-${row.itemId}`} warning content={`Fila ${row.rowNumber}: ${row.errors?.join(' · ') || 'Omitida.'}`} />)}
      </>}
      {error ? <Message negative content={error} /> : null}
      {preview && !progress ? <>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0' }}>
          <Label color="green">A importar: {validRows}</Label><Label>Grupos SKU: {preview.summary?.skuGroups || 0}</Label><Label>Publicaciones: {preview.summary?.affectedListings || 0}</Label><Label color={ignoredRows > 0 ? 'orange' : undefined}>Ignoradas: {ignoredRows}</Label>{invalidRows > 0 ? <Label color="red">Con errores: {invalidRows}</Label> : null}{warningRows > 0 ? <Label color="yellow">Advertencias: {warningRows}</Label> : null}
        </div>
        {ignoredRows > 0 ? <Message warning content="Cuando un SKU nuevo aparece más de una vez, se importará solo la fila con menor stock. Las demás se ignoran automáticamente." /> : null}
        {invalidRows > 0 ? <Message warning content="Las filas con errores se omitirán. Las demás se pueden importar." /> : null}
        <Table compact celled striped>
          <Table.Header><Table.Row><Table.HeaderCell>Fila</Table.HeaderCell><Table.HeaderCell>SKU actual</Table.HeaderCell><Table.HeaderCell>SKU nuevo</Table.HeaderCell><Table.HeaderCell>Stock</Table.HeaderCell><Table.HeaderCell>Estado</Table.HeaderCell></Table.Row></Table.Header>
          <Table.Body>{previewRows.map((row) => <Table.Row key={row.rowNumber} negative={row.outcome === 'INVALID'} warning={row.outcome === 'IGNORED' || row.warnings?.length > 0}><Table.Cell>{row.rowNumber}</Table.Cell><Table.Cell>{row.currentSku}</Table.Cell><Table.Cell>{row.nextSku}</Table.Cell><Table.Cell>{row.stock}</Table.Cell><Table.Cell>{row.outcome === 'APPLY' ? ['Se actualiza', ...row.warnings].join(' · ') : row.errors?.join(' · ') || 'Sin cambios'}</Table.Cell></Table.Row>)}</Table.Body>
        </Table>
      </> : null}
    </Modal.Content>
    <Modal.Actions>
      {completion ? <Button positive onClick={onClose}>Cerrar</Button> : null}
      {!progress ? <><Button onClick={onClose} disabled={loading}>Cancelar</Button><Button color="green" onClick={handleExecute} disabled={!preview?.draftId || validRows === 0 || loading} loading={loading}>Aplicar migracion</Button></> : null}
    </Modal.Actions>
  </Modal>;
}
